import { Agent } from "./Agent";
import { Call } from "./Call";

// Unfortunately this standard type is not known in Typescript
type HIDDevice = {
    vendorId: number;
    productId: number;
    productName: string;
    opened: boolean;
    collections: any[];
    open: () => Promise<void>;
    close: () => void;
    forget: () => void;
    oninputreport?: (event:any) => void;
    sendReport: (reportId:number,data: any) => Promise<void>;
    
    // custom: sends report of the current state to headset
    sendOutputReport?: (state:PrimaryCallState) => void;
   
    // if the device is controlling a foreign call from an other app
    foreignCall: boolean;
    foreignCallReleaseTimer: any;
    jabra: boolean;
}

type HID = {
    getDevices: () => Promise<HIDDevice[]>;
    requestDevice: (filter:any) => Promise<HIDDevice[]>;
    addEventListener: (name:string,listener:(event:any) => void) => void;
} 

// https://www.usb.org/sites/default/files/documents/hut1_12v2.pdf
const USAGE = {
    LED_MUTE: 0x080009, // 524297
    LED_OFF_HOOK: 0x080017, // 524311
    LED_RING: 0x080018, // 524312
    LED_HOLD: 0x080020, // 524320
    LED_SPEAKER: 0x08001e, // 524318
    LED_MICROPHONE: 0x080021, // 524321
    LED_ONLINE: 0x08002a, // 
    
    TELEPHONY_PAGE: 0x0b,
    TELEPHONY_HEADSET: 0x05,
    TELEPHONY_PROGRAMMABLE_BUTTON: 0x0b0007, // 720903
    TELEPHONY_HOOK_SWITCH: 0x0b0020, // 720928
    TELEPHONY_FLASH: 0x0b0021, // 720929
    TELEPHONY_PHONE_MUTE: 0x0b002f, // 720943
    TELEPHONY_LINE_BUSY_TONE: 0x0b0097, // 721047
    TELEPHONY_RINGER: 0x0b009e, // 
    TELEPHONY_REDIAL: 0x0b0024, // 720932
    TELEPHONY_SPEED_DIAL: 0x0b0050, // 720976

    //CONSUMER_00: 0x0c0000, // 786432 unassigned
    CONSUMER_MUTE: 0x0c00e2, // 786658
    CONSUMER_VOLUME_INCREMENT: 0x0c00e9, // 786665 RTC
    CONSUMER_VOLUME_DECREMENT: 0x0c00ea, // 786666 RTC
}

const DEVICE_FILTERS = { 
    filters: [{
        usagePage: USAGE.TELEPHONY_PAGE,
    }]
}

const JABRA_VENDOR_ID = 2830;

enum PrimaryCallState {
    IDLE = "IDLE",
    ALERTING = "ALERTING",
    CONNECTED = "CONNECTED",
    HOLD = "HOLD",
}

export class WebHID {
  
    private agent: Agent;
    private primaryCall?: Call;
    private secondaryCall?: Call;
    private primaryCallState = PrimaryCallState.IDLE;
    private devices: HIDDevice[] = [];
    private muted = false;
    private hid: HID;
    private setupId = 0;
    private autoCloseTimer: any;

    debounceTimeout = 10;
    autoCloseTimeout = 1000;
    foreignCallReleaseTimeout = 4000;    

    constructor(agent: Agent,hid: HID) {
        this.agent = agent;
        this.hid = hid;
	
        agent.on("primarycall",(event:any) => {
            this.primaryCall = event.call;
            if (!this.primaryCall || this.primaryCall.state=="null") {
                this.setPrimaryCallState(PrimaryCallState.IDLE,false);
            } else if (this.primaryCall.state=="alerting") {
                this.setPrimaryCallState(PrimaryCallState.ALERTING,!!this.primaryCall.muted)
            } else if (this.primaryCall.state=="connected") {
                this.setPrimaryCallState(PrimaryCallState.CONNECTED,!!this.primaryCall.muted)
            } else if (this.primaryCall.state=="hold") {
                this.setPrimaryCallState(PrimaryCallState.HOLD,!!this.primaryCall.muted)
            }
            this.updateSecondaryCall();
            this.startAutoCloseTimeout();
        });

        agent.on("call",() => this.updateSecondaryCall());

        this.hid.addEventListener("connect",(event:any ) => {
            agent.info(`webhid connect '${event.device.productName}`);   
            // delay setup until the device-collections are complete
            setTimeout(() => this.setupDevices([event.device]),1000);
        });

        this.hid.addEventListener("disconnect",(event:any) => {
            agent.info(`webhid disconnect '${event.device.productName}`);
            this.removeDevice(event.device);
        });
        
        window.addEventListener("onbeforeunload", () => {
           this.removeDevices();
        });
    }

    public setEnabled(value:boolean) {
        if (value) {
            this.bindDevices();
        } else {
            this.removeDevices();
        }
    }

    public async bindDevices() {
        const newDevices: HIDDevice[] = await this.hid.getDevices();
        this.setupDevices(newDevices);
    }

    updateSecondaryCall() {
        this.secondaryCall = undefined;
        if (this.primaryCall?.device) {
            for (const call of this.primaryCall.device.getCalls()) {
                if (call==this.primaryCall) continue;
                if (call.state=="alerting") {
                    this.secondaryCall = call;
                    break;
                } else if (!this.secondaryCall) {
                    this.secondaryCall = call;
                }
            }
        }
    }

    startAutoCloseTimeout() {
        clearTimeout(this.autoCloseTimer);
        if (!this.primaryCall) {
            this.autoCloseTimer = setTimeout(() => {
                if (!this.primaryCall) {
                    this.closeDevices();
                }
            }, this.autoCloseTimeout);
        }
    }

    closeDevices() {
        this.devices.forEach(device => {
            // Keep jabras open to trace foreign calls
            if (device.jabra) return;
          
            if (device.opened) {
                this.agent.debug(`webhid closing device '${device.productName}'`);
                device.close();
            }
        });
    }

    removeDevices() {
        this.agent.info("webhid removing devices");
        this.devices.forEach(device => this.removeDevice(device));
    }

    removeDevice(device:HIDDevice) {
        this.agent.info(`webhid removing ${device.productName}`);
        device.oninputreport = undefined;
        device.opened && device.close();
        this.devices = this.devices.filter(dev => dev!=device);
         this.agent.notify("webhid.device",{disconnected: device});
    }
     
    public async requestDevice() {
        const newDevices = await this.hid.requestDevice(DEVICE_FILTERS);
        this.setupDevices(newDevices);
    }

    public getDevices(): HIDDevice[] {
        return this.devices;
    }

    setPrimaryCallState(value:PrimaryCallState,muted:boolean) {
        this.agent.info(`webhid primaryCallState ${value} muted=${muted}`);
        this.primaryCallState = value;
        this.muted = muted;
        this.sendOutputReports();
    }

    setMute(value:boolean) {
        this.agent.info("webhid mute",value);
        this.muted = value;
        this.agent.notify("webhid.mute",{mute: value});
        if (!this.primaryCall) {
            // immediately send update, otherwise we wait for the primary call to report the state
            this.sendOutputReports();
        }
    }

    sendOutputReports() {
        this.devices.forEach(device => {
            
            // Don't send reports while device is in a foreign call
            if (device.foreignCall) return;

            device.sendOutputReport?.(this.primaryCallState);
        });
    }

    getUsageName(usage:number) {
        for (let prop in USAGE) {
            if (USAGE[prop as keyof typeof USAGE]==usage) {
                return prop;
            }
        }
        return usage.toString(16);
    }

    async setupDevices(newDevices: HIDDevice[]) {
        for (const device of newDevices) {
            this.setupDevice(device);
            if (!this.devices.includes(device)) {
                this.devices.push(device);
            }
            await this.openDevice(device);
        }
        this.sendOutputReports()
    }

    async openDevice(device:HIDDevice) {
        if (!device.opened) {
            try {
                this.agent.debug(`webhid opening device '${device.productName}'`);
                await device.open();
            } catch(error:any) {
                this.agent.warn(`webhid cannot open device '${device.productName}'`,error);
            }
        }
    }

    setupDevice(device:HIDDevice) {
        const setupId = ++this.setupId;
    
        this.agent.info(`webhid setup productName=${device.productName} vendorId=${device.vendorId} productId=${device.productId} setupId=${setupId}`);
 
        device.jabra = device.vendorId==JABRA_VENDOR_ID;
      
        let lastReportValue: number[] = [];
        let outputReportId = 0;
        let outputReportLedOffHook  = 0;
        let outputReportLedRing  = 0;
        let outputReportLedHold  = 0;
        let outputReportLedMute  = 0;
        let flashTime = 0;
      
        let debounceTimer: any = {};
        
        let processHookSwitch: boolean|null = null;
        let processPhoneMute: boolean|null = null;
        let processFlash: boolean|null = null;
        let processProgrammableButton: boolean|null = null;
        let processVolumeIncrement: boolean|null = null;
        let processVolumeDecrement: boolean|null = null;
   
        for (const collection of device.collections) {
            for (const report of collection.outputReports) {
                if (collection.usagePage==USAGE.TELEPHONY_PAGE) {
                    let bitpos = 0;
                    outputReportId = report.reportId;
                    report.items?.forEach((item:any) => {
                        this.agent.debug(`webhid ${device.productName} multiple usages per item: ${item.usages} reportCount=${item.reportCount} reportSize=${item.reportSize}`);
                        item.usages?.forEach((usage:number) => {
                            if (usage==USAGE.LED_OFF_HOOK) {
                                outputReportLedOffHook = 1<<bitpos;
                                this.agent.debug(`webhid ${device.productName} LED_OFF_HOOK reportId=${report.reportId} mask=${outputReportLedOffHook}`);
                            } else if (usage==USAGE.LED_RING) {
                                outputReportLedRing = 1<<bitpos;
                                this.agent.debug(`webhid ${device.productName} LED_RING reportId=${report.reportId} mask=${outputReportLedRing}`);
                            } else if (usage==USAGE.LED_HOLD) {
                                outputReportLedHold = 1<<bitpos;
                                this.agent.debug(`webhid ${device.productName} LED_HOLD reportId=${report.reportId} mask=${outputReportLedHold}`);
                            } else if (usage==USAGE.LED_MUTE) {
                                outputReportLedMute = 1<<bitpos;
                                this.agent.debug(`webhid ${device.productName} LED_MUTE reportId=${report.reportId} mask=${outputReportLedMute}`);
                            } else {
                                this.agent.debug(`webhid ${device.productName} OTHER reportId=${report.reportId} usage=${usage}/${this.getUsageName(usage)} mask=${1<<bitpos}`);
                            }

                            // Ugly, but Jabra on MAC reports the LED-flags in one item but with multiple usages.
                            // To avoid that we always send the same flag we have to increment the bitpos manually.
                            if (item.usages.length>1 && item.reportCount>1) {
                                bitpos++;
                                this.agent.debug(`webhid Jabra-fix: increment reportCount=${item.reportCount} reportSize=${item.reportSize} nextMask=${1<<bitpos}`);
                            }
                        });

                        bitpos += item.reportCount*item.reportSize;
                    });
                }
            }

            // just for debugging
            for (const report of collection.inputReports) {
                report.items?.forEach((item:any) => {
                    item.usages?.forEach((usage:number) => {
                        this.agent.debug(`webhid ${device.productName} ITEM IN reportId=${report.reportId} usage=${usage}/${this.getUsageName(usage)}`);
                    });
                });
            }
        }

        // I separated the processing of the received WebHID request from the parsing.
        // This allows to work on a consolidated state without having intermediate/temporary requests.
        // This method here processes the evaluated commands.
        const processChanges = () => {
            this.agent.info(`webhid ${device.productName} process hook=${processHookSwitch} mute=${processPhoneMute} flash=${processFlash} button=${processProgrammableButton} foreign=${device.foreignCall}`);
                  
            const now = Date.now();
            if (device.jabra && processHookSwitch!=null && (now-flashTime)<500) {
                this.agent.info(`webhid ${device.productName} ignore TELEPHONY_HOOK_SWITCH after FLASH`); 
                processHookSwitch = null;
            }

            if (processHookSwitch!=null) {
                if (processHookSwitch) {
                    // hook off
                    if (this.primaryCallState==PrimaryCallState.ALERTING) {
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_HOOK_SWITCH, answering call`);
                        this.primaryCall?.answerCall();
                    } else if (!this.primaryCall) {
                        // hook-off without a primary call -> this must be a foreign call
                        device.foreignCall = true;
                        clearTimeout(device.foreignCallReleaseTimer);
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_HOOK_SWITCH, foreign call`);
                    }
                } else if (device.foreignCall) {
                    // foreign call is gone. 
                    if (device.jabra) {
                         // Jabra-headsets that were allocated by Teams will send a second hook-on after nearly 4 seconds.
                         // (maybe when teams releases the headset?). Therefore we have to wait some time before
                         // binding our own call to the headset. Otherwise our call will be cleared.
                        clearTimeout(device.foreignCallReleaseTimer);
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_HOOK_SWITCH, foreign call ended`);
                        device.foreignCallReleaseTimer = setTimeout(() => {
                            device.foreignCall = false;
                            if (this.primaryCall && !device.foreignCall) {
                                this.agent.info(`webhid ${device.productName} process TELEPHONY_HOOK_SWITCH, rebind call`);
                                device.sendOutputReport?.(this.primaryCallState);
                            }
                        },this.foreignCallReleaseTimeout);

                    } else if (this.primaryCall) {
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_HOOK_SWITCH, rebind call`);
                        device.sendOutputReport?.(this.primaryCallState);
                    }

                } else {
                    if (this.primaryCall && this.primaryCallState!=PrimaryCallState.HOLD) {
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_HOOK_SWITCH, clearing call`);
                        this.primaryCall?.clearConnection();
                        
                        // Jabra-headsets seem to be defiant if we immediately report the next waiting call.
                        // => They try to retrieve it and then clear it as well.
                        // To solve the problem we simply just report an idle phone and headset believes that 
                        // the call-termination worked.
                        if (device.jabra) {
                            device.sendOutputReport?.(PrimaryCallState.IDLE);
                        }
                    }  
                }
                processHookSwitch = null;
            }

            if (device.foreignCall) {
                if (processFlash) {
                    flashTime = Date.now();
                }
                processPhoneMute = null;
                processFlash = null;
                processProgrammableButton = null;
                processVolumeIncrement = null;
                processVolumeDecrement = null;
                return;
            }

            if (processPhoneMute!=null) {
                this.agent.info(`webhid ${device.productName} process TELEPHONY_PHONE_MUTE, set muted ${processPhoneMute}`);
                this.setMute(processPhoneMute);
                processPhoneMute = null;

                // It seems drivers on MAC insert a FLASH when sending MUTE (checked with Yealink and Jabra) .
                // This would then immediately put the call on hold. Therefore we simply ignore FLASH when MUTE was received
                if (processFlash) {
                    this.agent.info(`webhid ${device.productName} ignore flash ${processFlash} when processing mute`);
                    processFlash = null;
                }
            }
            if (processFlash!=null) {
                if (processFlash) {
                    if (this.secondaryCall?.state=="alerting") {
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_FLASH, answer secondary call`);
                        this.secondaryCall.answerCall();
                    } else if (this.secondaryCall?.state=="hold") {
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_FLASH, retrieve secondary call`);
                        this.secondaryCall.retrieveCall();
                    } else if (this.primaryCallState==PrimaryCallState.HOLD) {
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_FLASH, retrieving call`);
                        this.primaryCall?.retrieveCall();
                    } else if (this.primaryCallState==PrimaryCallState.CONNECTED) {
                        this.agent.info(`webhid ${device.productName} process TELEPHONY_FLASH, holding call`);
                        this.primaryCall?.holdCall();
                    }
                }
               processFlash = null;
            }
            if (processProgrammableButton!=null) {
                if (processProgrammableButton) {
                    this.agent.info(`webhid ${device.productName} process TELEPHONY_PROGRAMMABLE_BUTTON, clearing call`);
                    this.primaryCall?.clearConnection();
                }
                processProgrammableButton = null;
            }
            if (processVolumeIncrement) {
                if (processVolumeIncrement) {
                    this.agent.info(`webhid ${device.productName} process CONSUMER_VOLUME_INCREMENT`);
                    this.agent.notify("webhid.volume",{mode:"increment"});
                }
                processVolumeIncrement = null;
            }
            if (processVolumeDecrement) {
                if (processVolumeDecrement) {
                    this.agent.info(`webhid ${device.productName} process CONSUMER_VOLUME_DECREMENT`);
                    this.agent.notify("webhid.volume",{mode:"decrement"});
                }
               processVolumeDecrement = null;
            }
        }

        // Just parses the received WebHID messages and stores the detected commands.
        // Then it starts a debouncing-timer to process the commands later in "processChanges()"
        device.oninputreport = (event:any) => {
            const reportId = event.reportId;
            const value = event.data.getUint8(0);
            const delta = value ^ lastReportValue[reportId];
            lastReportValue[reportId] = value;
  
            this.agent.debug(`webhid ${device.productName} setupId=${setupId} reportId=${reportId} value=${value} changed=${delta}`);
          
            for (const collection of device.collections) {
                for (const inputReport of collection.inputReports) {
                    if (inputReport.reportId!=event.reportId) continue;
                    
                    let bitpos = 0;
                    inputReport.items?.forEach((item:any) => {
                        item.usages?.forEach((usage:number) => {
                            const flag = 1<<bitpos;
                            const on = !!(value & flag);
                            const changed = !!(flag & delta);

                            if (usage==USAGE.TELEPHONY_HOOK_SWITCH) {
                                if (changed) processHookSwitch = on;
                                this.agent.debug(`webhid ${device.productName} sent TELEPHONY_HOOK_SWITCH changed=${changed} on=${on} process=${processHookSwitch}`);
                            } else if (usage==USAGE.TELEPHONY_PHONE_MUTE) {
                                if (changed && on) {
                                    if (processPhoneMute==null) {
                                        processPhoneMute = this.muted; // use what we know
                                    }
                                    processPhoneMute = !processPhoneMute;  // toggle
                                }
                                this.agent.debug(`webhid ${device.productName} sent TELEPHONY_PHONE_MUTE changed=${changed} on=${on} process=${processPhoneMute}`);
                            } else if (usage==USAGE.TELEPHONY_FLASH) {
                                if (changed) processFlash = true;
                                this.agent.debug(`webhid ${device.productName} sent TELEPHONY_FLASH changed=${changed} on=${on} process=${processFlash}`);
                            } else if (usage==USAGE.TELEPHONY_PROGRAMMABLE_BUTTON) {
                                if (changed) processProgrammableButton = true;
                                this.agent.debug(`webhid ${device.productName} sent TELEPHONY_PROGRAMMABLE_BUTTON changed=${changed} on=${on} process=${processProgrammableButton}`);
                            } else if (usage==USAGE.CONSUMER_VOLUME_INCREMENT) {
                                if (changed) processVolumeIncrement = true;
                                this.agent.debug(`webhid ${device.productName} sent CONSUMER_VOLUME_INCREMENT changed=${changed} on=${on} process=${processVolumeIncrement}`);
                            } else if (usage==USAGE.CONSUMER_VOLUME_DECREMENT) {
                                if (changed) processVolumeDecrement = true;
                                this.agent.debug(`webhid ${device.productName} sent CONSUMER_VOLUME_DECREMENT changed=${changed} on=${on} process=${processVolumeDecrement}`);
                            }
                        });
                        bitpos += item.reportCount*item.reportSize;
                    });
                }
            }             

            // wait a little until the messages consolidated the current state
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(processChanges,this.debounceTimeout);
        }

        device.sendOutputReport = async (state:PrimaryCallState) => {
            if (outputReportId) {
                let value = 0;
                if (state==PrimaryCallState.CONNECTED) {
                    value = outputReportLedOffHook;
                } else if (state==PrimaryCallState.ALERTING) {
                    value = outputReportLedRing;
                } else if (state==PrimaryCallState.HOLD) {
                    value = outputReportLedHold;
                }
                if (this.muted) {
                    value = value | outputReportLedMute;
                }
                this.agent.info(`webhid sending setupId=${setupId} reportId=${outputReportId} [${value}] to ${device.productName} (callID=${this.primaryCall?.callID},state=${state}/${this.primaryCallState},muted=${this.muted})`);
                await this.openDevice(device);
                try {
                    await device.sendReport(outputReportId, new Uint8Array([value]));
                } catch(error: any) {
                    this.agent.error(`webhid cannot send report to '${device.productName}' setupId=${setupId}`,error);    
                }
                this.startAutoCloseTimeout();
            }
        }

        this.agent.notify("webhid.device",{connected: device});
    }

}


